import { FC, Fragment } from 'react';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { monthDayCellViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { CaseDto } from '@app/shared/models/contracts/project-dto';
import {
  TABLE_COL_WIDTH_ALTERNATIVE,
  TABLE_LABELS_COL_WIDTH_ALTERNATIVE,
} from '@app/shared/constants/table-config';
import styles from './discount-rate-override.module.scss';
import useDiscountRateOverride from './useDiscountRateOverride';
import useDiscountRateOverrideExpansion from './useDiscountRateOverrideExpansion';
import DiscountRateOverrideInstrumentSection from './DiscountRateOverrideInstrumentSection';

const DiscountRateOverrideTable: FC = () => {
  const {
    formMethods,
    formSubmitHandler,
    initializedCases,
    casesStructure,
    sortedValuedInstruments,
    columnsCount,
    handleRevertCategory,
    findSortedCaseIndex,
  } = useDiscountRateOverride();
  const { expandedInstruments, toggleInstrumentExpanded } =
    useDiscountRateOverrideExpansion(sortedValuedInstruments);

  return (
    <FormProvider {...formMethods}>
      <form
        data-testid="discount-rate-override-form"
        onBlur={formMethods.handleSubmit(formSubmitHandler as SubmitHandler<{ cases: CaseDto[] }>)}>
        <table
          className={`table-primary table-primary--zebra table-primary--fixed table-primary ${styles['sticky-first-column']}`}>
          <colgroup>
            <col style={{ width: `${TABLE_LABELS_COL_WIDTH_ALTERNATIVE}px` }} />
            {[...Array(columnsCount - 1)].map((_, index) => (
              <col key={`col-${index}`} style={{ width: `${TABLE_COL_WIDTH_ALTERNATIVE}px` }} />
            ))}
          </colgroup>
          <thead className="table-primary__sticky-section table-primary__sticky-section--table-single-row-header">
            <tr>
              <th className="table-primary__cell--header-tertiary table-primary__cell--vertical-separator" />
              {casesStructure.map((caseItem, index) => (
                <Fragment key={caseItem.caseId + '-case-header-' + index}>
                  <th
                    colSpan={caseItem.forecasts.length}
                    className="table-primary__cell--header-tertiary table-primary__cell--center table-primary__cell--vertical-separator table-primary__cell--group-separator-vertical">
                    <span>{caseItem.narrative}</span>
                  </th>
                </Fragment>
              ))}
            </tr>
            <tr>
              <th className="table-primary__cell--vertical-separator" />
              {casesStructure.map((caseItem) => {
                return caseItem.forecasts.map((forecast, index) => (
                  <Fragment key={forecast.forecastId + '-forecast-year'}>
                    <td
                      className={classNames({
                        'table-primary__cell--vertical-separator':
                          index === caseItem.forecasts.length - 1,
                      })}>
                      <CellValue value={forecast.forecastYear} {...monthDayCellViewRules} />
                    </td>
                  </Fragment>
                ));
              })}
            </tr>
          </thead>
          <tbody>
            {sortedValuedInstruments.map((instrument) => (
              <DiscountRateOverrideInstrumentSection
                key={`instrument-section-${instrument.instrumentId}`}
                instrument={instrument}
                columnsCount={columnsCount}
                initializedCases={initializedCases}
                casesStructure={casesStructure}
                findSortedCaseIndex={findSortedCaseIndex}
                handleRevertCategory={handleRevertCategory}
                isExpanded={expandedInstruments[instrument.instrumentId] ?? false}
                toggleInstrumentExpanded={toggleInstrumentExpanded}
              />
            ))}
          </tbody>
        </table>
      </form>
    </FormProvider>
  );
};

export default DiscountRateOverrideTable;
